// DOMPurify wrapper for content that must be rendered as HTML.
//
// ``marked`` (and any other HTML we take from an external source — the AI
// service, notifications, comments) goes through this before it reaches
// ``dangerouslySetInnerHTML``. Closes AUDIT.md C2.
//
// The default config below allows the subset of tags the AI chat actually
// emits: paragraphs, lists, links, emphasis, code, blockquotes, line
// breaks. Anything else (script, iframe, on* handlers, javascript: URLs,
// data: URLs except images) is stripped.

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  'span',
  'strong',
  'ul',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel'];

function configure() {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('rel', 'noopener noreferrer nofollow');
      node.setAttribute('target', '_blank');
    }
  });
}

let configured = false;

export function sanitizeHtml(input) {
  if (!configured) {
    configure();
    configured = true;
  }
  if (input === null || input === undefined) return '';
  return DOMPurify.sanitize(String(input), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
  });
}

// Richer allowlist for chapter HTML rendered in the preview view. TipTap
// emits images, task lists (``data-type``/``data-checked``), text-align
// styles, and font/color/highlight spans; ``sanitizeHtml`` strips all of
// those. We keep ``<script>``, ``<iframe>``, ``on*`` handlers, and
// ``javascript:`` URLs blocked — DOMPurify handles them by default even
// with the expanded tag list.
const RICH_ALLOWED_TAGS = [
  ...ALLOWED_TAGS,
  'img',
  'figure',
  'figcaption',
  'mark',
  'sub',
  'sup',
  's',
  'u',
  'del',
  'ins',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'div',
  'input',
];

const RICH_ALLOWED_ATTR = [
  ...ALLOWED_ATTR,
  'src',
  'alt',
  'width',
  'height',
  'style',
  'class',
  'colspan',
  'rowspan',
  'align',
  'valign',
  'loading',
  'type',
  'checked',
  'disabled',
];

export function sanitizeRichHtml(input) {
  if (!configured) {
    configure();
    configured = true;
  }
  if (input === null || input === undefined) return '';
  return DOMPurify.sanitize(String(input), {
    ALLOWED_TAGS: RICH_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_ALLOWED_ATTR,
    // TipTap's task list uses ``data-type="taskList"`` /
    // ``data-checked="true"`` on its ``<ul>`` / ``<li>`` nodes so the
    // typography CSS can style them. Allow data-* but keep the tag /
    // attr allowlists authoritative.
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
  });
}

/**
 * Decode a base64 URL segment, returning ``null`` when the value is malformed
 * rather than throwing. Closes AUDIT.md M6 — raw ``atob()`` throws on
 * hand-edited URLs and crashes the component tree.
 */
export function safeAtob(value) {
  if (!value) return null;
  try {
    return atob(String(value));
  } catch {
    return null;
  }
}

/**
 * Return true when the value is a safe navigation target (``http(s)://`` URL
 * or a relative path). Closes AUDIT.md H5.
 */
export function isSafeHref(value) {
  if (!value) return false;
  if (typeof value !== 'string') return false;
  // Protocol-relative URLs (``//evil.example.com``) resolve against the
  // current origin's scheme — benign-looking but trivially hijackable via
  // cache poisoning / downgrade. Reject before ``new URL`` can paper over.
  if (value.startsWith('//')) return false;
  if (value.startsWith('/')) return true;
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}
